import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

type StatCardVariant = 'default' | 'brand'

const variantStyles: Record<
  StatCardVariant,
  { card: string; title: string; icon: string; value: string }
> = {
  default: {
    card: 'bg-surface-brand-L1',
    title: 'text-content-primary-subtle',
    icon: 'text-content-primary-subtle',
    value: 'text-content-primary-bold',
  },
  brand: {
    card: 'bg-content-brand-bold',
    title: 'text-content-brand-light',
    icon: 'text-content-brand-subtle',
    value: 'text-content-brand-subtle',
  },
}

interface StatCardProps {
  /** Uppercase label shown in the top row, beside the icon. */
  label: string
  value: string | number
  /** Rendered at 16px in the top-right; color is driven by the variant via `currentColor`. */
  icon?: ReactNode
  /** Optional small caption shown below the value. */
  caption?: string
  variant?: StatCardVariant
  className?: string
}

export function StatCard({
  label,
  value,
  icon,
  caption,
  variant = 'default',
  className,
}: StatCardProps) {
  const styles = variantStyles[variant]

  return (
    <div
      className={cn(
        'flex flex-col items-start justify-center rounded-xl p-4',
        styles.card,
        className
      )}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <p
          className={cn(
            'text-xs font-semibold uppercase leading-4',
            styles.title
          )}
        >
          {label}
        </p>
        {icon && (
          <span className={cn('flex shrink-0 [&>svg]:size-4', styles.icon)}>
            {icon}
          </span>
        )}
      </div>
      <p
        className={cn(
          'text-[22px] font-bold leading-7 tracking-[-1px]',
          styles.value
        )}
      >
        {value}
      </p>
      {caption && (
        <p className={cn('text-[10px] font-semibold leading-4', styles.title)}>
          {caption}
        </p>
      )}
    </div>
  )
}
